"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { hasInstalledApp, reportAppOpened } from "@/app/actions/app-install";
import { getPushKey, savePushSubscription } from "@/app/actions/web-push";
import { Z_OFFER_POPUP } from "@/components/layout/zIndex";
import { detectDevice } from "./detect-device";
import { useBelowHeader } from "./use-below-header";

/**
 * A popup: "Install Green Tourism Pakistan on your phone." with one Install button
 * (owner, 1 Oct 2026: keep it simple, one click). Also registers the service
 * worker (public/sw.js) in production.
 *
 * Shown only when the site is not installed: never inside the installed
 * version, never after the browser reports an install (appinstalled, or
 * getInstalledRelatedApps via the manifest's related_applications), and not
 * for 24 hours after × (owner, 2 Oct 2026; it was 30 days).
 *
 * Install where the browser allows a one-click install (Chrome, Edge) opens
 * its prompt. iPhones, and browsers without that prompt, cannot be installed
 * by a website, so the same button shows the steps instead. Phones read
 * "Install Green Tourism Pakistan on your mobile." Computers (owner, 1 Oct
 * 2026, option B) read "Install Green Tourism Pakistan." with Install (this
 * computer) and Phone (a QR code of the site, public/icons/site-qr.svg).
 *
 * Once the install offer is out of the way (installed, or ×), the same popup
 * offers notifications instead: "Get alerts for new tours and offers." with
 * Allow. Shown only where the browser can receive them (on iPhone that means
 * the installed version only), only while the visitor has not answered the
 * browser's question yet, and only when the server has its push keys.
 */
type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
type RelatedApp = { platform: string; url?: string };

const DISMISSED_KEY = "gtp-install-dismissed";
const INSTALLED_KEY = "gtp-installed";
const ALERTS_DISMISSED_KEY = "gtp-alerts-dismissed";
const ALERTS_SYNCED_KEY = "gtp-alerts-synced";
const OPEN_REPORTED_KEY = "gtp-app-open-reported";
const HOUR = 60 * 60 * 1000;
/** How long × keeps a card away before it comes back. */
const QUIET_MS = 24 * HOUR;
/** iPhone: after Install shows the Share steps, most people do add it, so a week, not a day. */
const IOS_TRIED_MS = 7 * 24 * HOUR;
/** When each card slides in after the page opens, so they arrive one by one. */
const INSTALL_DELAY_MS = 1200;
const ALERT_AFTER_INSTALL_DELAY_MS = 8000;
const ALERT_ALONE_DELAY_MS = 1200;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: nothing to remember */
  }
}
/** Each dismissal stores the time it ends ("quiet until"). */
function quietUntil(key: string): boolean {
  return Number(read(key) ?? 0) > Date.now();
}
function snooze(key: string, ms: number) {
  write(key, String(Date.now() + ms));
}

function canPush(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** The server key as bytes: older Safari refuses it as text. */
function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** The browser's subscription for this site, created with the server's key if needed, saved on the server. */
async function subscribe(key: string): Promise<boolean> {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ?? (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  return savePushSubscription(subscription.toJSON());
}

export function AppInstall() {
  const pathname = usePathname();
  const [device, setDevice] = useState<"ios" | "phone" | "desktop" | null>(null);
  // Android browser built into another app (Google Lens, the camera's QR
  // reader, Facebook, Instagram…): it cannot install, Chrome can.
  const [inAppBrowser, setInAppBrowser] = useState(false);
  const [installed, setInstalled] = useState(true); // assume installed until checked
  const [quiet, setQuiet] = useState(true);
  const [alertsQuiet, setAlertsQuiet] = useState(true);
  const [pushKey, setPushKey] = useState<string | null>(null);
  const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);
  const [showSteps, setShowSteps] = useState(false);
  const [showComputerSteps, setShowComputerSteps] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {});
    }

    const markInstalled = () => {
      write(INSTALLED_KEY, "1");
      setInstalled(true);
    };
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;

    const ua = navigator.userAgent;
    const { ios, android, inAppBrowser: inApp } = detectDevice({
      userAgent: ua,
      maxTouchPoints: navigator.maxTouchPoints,
      hasStandalone: "standalone" in navigator,
      platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform,
    });
    const phone = android || window.matchMedia("(max-width: 900px) and (pointer: coarse)").matches;
    const platform = ios ? "ios" : android ? "android" : "desktop";

    const check = async () => {
      setDevice(ios ? "ios" : phone ? "phone" : "desktop");
      setInAppBrowser(inApp);
      if (standalone) {
        // The installed app, opened: remember it on the account (once a
        // session), so Safari on the same iPhone stops offering the install.
        try {
          if (sessionStorage.getItem(OPEN_REPORTED_KEY) !== "1") {
            sessionStorage.setItem(OPEN_REPORTED_KEY, "1");
            void reportAppOpened(platform).catch(() => {});
          }
        } catch {
          /* no session storage */
        }
        return markInstalled();
      }
      if (read(INSTALLED_KEY) === "1") return markInstalled();
      // Safari on an iPhone cannot see the installed app; the account can.
      if (ios && (await hasInstalledApp("ios").catch(() => false))) return markInstalled();
      const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<RelatedApp[]> };
      if (nav.getInstalledRelatedApps) {
        const apps = await nav.getInstalledRelatedApps().catch(() => [] as RelatedApp[]);
        if (apps.length > 0) return markInstalled();
      }
      setInstalled(false);
    };
    void check();

    // Alerts: only where push works and the visitor has not answered yet.
    // Someone who already said yes is re-saved once a session, so a browser
    // that renewed its subscription is not lost.
    if (process.env.NODE_ENV === "production" && canPush() && Notification.permission !== "denied") {
      void getPushKey()
        .then((key) => {
          if (!key) return;
          if (Notification.permission === "granted") {
            let synced = false;
            try {
              synced = sessionStorage.getItem(ALERTS_SYNCED_KEY) === "1";
              sessionStorage.setItem(ALERTS_SYNCED_KEY, "1");
            } catch {
              /* no session storage: sync anyway */
            }
            if (!synced) void subscribe(key).catch(() => {});
            return;
          }
          setPushKey(key);
        })
        .catch(() => {});
    }

    // The event itself is caught by public/pbd-install.js as the page loads
    // (it can fire before this runs); pick it up from there, now or later.
    const takePrompt = () => {
      const caught = (window as Window & { __gtpInstallPrompt?: InstallPromptEvent | null }).__gtpInstallPrompt;
      if (!caught) return;
      setPromptEvent(caught);
      // The browser only offers this while the site is not installed.
      setInstalled(false);
    };
    const firstLook = window.setTimeout(takePrompt, 0);
    window.addEventListener("gtp-installable", takePrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.clearTimeout(firstLook);
      window.removeEventListener("gtp-installable", takePrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  // The 24-hour dismissals are re-checked on every page.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuiet(quietUntil(DISMISSED_KEY));
      setAlertsQuiet(quietUntil(ALERTS_DISMISSED_KEY));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  const offerInstall = !installed && !quiet && device !== null;
  const computer = device === "desktop";
  const canAlert = pushKey !== null && !alertsQuiet;
  const offerAlerts = !offerInstall && canAlert;

  const allow = async () => {
    if (!pushKey) return;
    const answer = await Notification.requestPermission().catch(() => "denied" as NotificationPermission);
    if (answer === "granted") await subscribe(pushKey).catch(() => false);
    // Answered either way: the browser now remembers, so the offer is done.
    snooze(ALERTS_DISMISSED_KEY, QUIET_MS);
    setPushKey(null);
  };

  const dismissAlerts = () => {
    snooze(ALERTS_DISMISSED_KEY, QUIET_MS);
    setAlertsQuiet(true);
  };
  // Website visitors get the alerts offer too (owner, 2 Oct 2026: "enable web
  // notifications as well"), as its own card below the install card, arriving
  // a few seconds after it ("show them separately ... with intervals"). Only
  // where the browser can receive alerts — not iPhone Safari, which allows
  // them in the installed app only.
  const alertsCard = (delayMs: number) => (
    <Popup
      label="Notifications from Green Tourism Pakistan"
      text="Get alerts for new tours and offers."
      action="Allow"
      icon={<BellIcon />}
      delayMs={delayMs}
      onAction={allow}
      onDismiss={dismissAlerts}
    />
  );

  if (offerAlerts) return <PopupStack>{alertsCard(ALERT_ALONE_DELAY_MS)}</PopupStack>;

  if (!offerInstall) return null;

  /**
   * Opens the browser's own install box. On Android Chrome the event can
   * arrive a moment after the page appears, so a tap that beats it waits up to
   * 1.5 s for it before falling back to the menu steps.
   */
  const promptInstall = async (): Promise<boolean> => {
    let event = promptEvent;
    if (!event && device === "phone") {
      await new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, 1500);
        window.addEventListener(
          "gtp-installable",
          () => {
            window.clearTimeout(timer);
            resolve();
          },
          { once: true },
        );
      });
      event = (window as Window & { __gtpInstallPrompt?: InstallPromptEvent | null }).__gtpInstallPrompt ?? null;
    }
    if (!event) return false;
    await event.prompt();
    const choice = await event.userChoice.catch(() => null);
    setPromptEvent(null);
    (window as Window & { __gtpInstallPrompt?: InstallPromptEvent | null }).__gtpInstallPrompt = null;
    if (choice?.outcome === "accepted") {
      write(INSTALLED_KEY, "1");
      setInstalled(true);
    }
    return true;
  };

  const install = async () => {
    if (await promptInstall()) return;
    // iPhone: the Share steps are the install; give it a week before asking again.
    if (device === "ios") snooze(DISMISSED_KEY, IOS_TRIED_MS);
    setShowSteps((open) => !open);
  };

  /** iPhone Safari: "Already installed" — never offer the install here again. */
  const alreadyInstalled = () => {
    write(INSTALLED_KEY, "1");
    setInstalled(true);
  };

  const dismiss = () => {
    snooze(DISMISSED_KEY, QUIET_MS);
    setQuiet(true);
  };

  // Computers: the QR code for the phone is always showing; Install puts it on
  // this computer (browser prompt, or the steps where there is none).
  const installHere = async () => {
    if (await promptInstall()) return;
    setShowComputerSteps((open) => !open);
  };
  const computerSteps = showComputerSteps ? (
    <p className="m-0 mt-3 leading-snug text-[var(--chrome-muted)]">
      Click the <strong className="text-[var(--chrome-fg)]">install icon</strong> at the right end of the address bar,
      or open the browser menu <strong className="text-[var(--chrome-fg)]">⋮</strong> and choose{" "}
      <strong className="text-[var(--chrome-fg)]">Install</strong>.
    </p>
  ) : null;
  const qr = (
    <div className="mt-3 flex items-center gap-3">
      {/* eslint-disable-next-line @next/next/no-img-element -- a static QR code of the site */}
      <img src="/icons/site-qr.svg" alt="QR code for greenpaktourism.com" width={88} height={88} className="shrink-0 rounded bg-white p-1" />
      <p className="m-0 leading-snug text-[var(--chrome-muted)]">
        On your phone? Scan with the camera, then tap <strong className="text-[var(--chrome-fg)]">Install</strong>.
      </p>
    </div>
  );

  if (computer) {
    return (
      <PopupStack>
        <Popup
          label="Install Green Tourism Pakistan"
          text="Install Green Tourism Pakistan."
          action="Install"
          expanded={promptEvent ? undefined : showComputerSteps}
          onAction={installHere}
          onDismiss={dismiss}
        >
          {qr}
          {computerSteps}
        </Popup>
        {canAlert ? alertsCard(ALERT_AFTER_INSTALL_DELAY_MS) : null}
      </PopupStack>
    );
  }
  return (
    <PopupStack>
      <Popup
        label="Install Green Tourism Pakistan"
        text="Install Green Tourism Pakistan on your mobile."
        action="Install"
        expanded={promptEvent ? undefined : showSteps}
        onAction={install}
        onDismiss={dismiss}
      >
        {showSteps ? (
          <p className="m-0 mt-2 pl-11 leading-snug text-[var(--chrome-muted)]">
            {device === "ios" ? (
              <>
                Tap <strong className="text-[var(--chrome-fg)]">Share</strong>, then{" "}
                <strong className="text-[var(--chrome-fg)]">Add to Home Screen</strong>.
              </>
            ) : inAppBrowser ? (
              <>
                This page opened inside another app, which cannot install it. Tap{" "}
                <strong className="text-[var(--chrome-fg)]">⋮</strong> and choose{" "}
                <strong className="text-[var(--chrome-fg)]">Open in Chrome</strong>, then tap{" "}
                <strong className="text-[var(--chrome-fg)]">Install</strong> there.
              </>
            ) : (
              <>
                Open your browser menu <strong className="text-[var(--chrome-fg)]">⋮</strong>, then tap{" "}
                <strong className="text-[var(--chrome-fg)]">Install app</strong> or{" "}
                <strong className="text-[var(--chrome-fg)]">Add to Home screen</strong>.
              </>
            )}
          </p>
        ) : null}
        {device === "ios" ? (
          <button
            type="button"
            onClick={alreadyInstalled}
            className="mt-2 ml-11 cursor-pointer bg-transparent p-0 text-[12px] font-semibold text-[var(--chrome-muted)] underline underline-offset-2 hover:text-[var(--chrome-fg)]"
          >
            Already installed
          </button>
        ) : null}
      </Popup>
      {canAlert ? alertsCard(ALERT_AFTER_INSTALL_DELAY_MS) : null}
    </PopupStack>
  );
}

/**
 * The offers' popup, on phones and computers alike (owner, 1 Oct 2026: "the
 * side float and separate float is required", not a strip): a small card
 * floating over the page, just below the header and under the logo (owner, 1 Oct 2026: "left side under green pak logo ...
 * separate, closable"), with icon, sentence, button and ×. It slides in a
 * moment after the page opens (pbd-offer-popup in globals.css).
 */
/**
 * Where the cards float: just below the site header, under the logo,
 * following the header's bottom edge as it shrinks on scroll; never part of
 * the header. Cards stack downwards with a gap, each one separate.
 */
function PopupStack({ children }: { children: React.ReactNode }) {
  const top = useBelowHeader();
  return (
    <div
      style={{ top: top ?? 0, visibility: top === null ? "hidden" : undefined }}
      className={`pbd-offer-stack fixed inset-x-3 ${Z_OFFER_POPUP} flex max-w-[360px] flex-col gap-3 md:right-auto md:left-4 md:w-[400px] md:max-w-none`}
    >
      {children}
    </div>
  );
}

function Popup({
  label,
  text,
  action,
  expanded,
  icon,
  delayMs = INSTALL_DELAY_MS,
  onAction,
  onDismiss,
  children,
}: OfferProps) {
  return (
    <div
      role="dialog"
      aria-label={label}
      style={{ animationDelay: `${delayMs}ms` }}
      className="pbd-offer-popup rounded-xl border border-[var(--chrome-border)] bg-[var(--chrome-bg)] p-4 text-[14px] text-[var(--chrome-fg)] shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
    >
      <div className="flex items-center gap-3">
        {icon ?? (
          // eslint-disable-next-line @next/next/no-img-element -- a 32px static icon
          <img src="/icons/app-192.png" alt="" width={32} height={32} className="shrink-0 rounded-md" />
        )}
        <p className="m-0 flex-1 leading-snug">{text}</p>
        <button
          type="button"
          onClick={onAction}
          aria-expanded={expanded}
          className="shrink-0 cursor-pointer rounded bg-[var(--cta)] px-3 py-1.5 text-[13px] font-bold text-[var(--chrome-bg)]"
        >
          {action}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-my-1 -mr-1 inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded bg-transparent text-lg leading-none text-[var(--chrome-muted)]"
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

function BellIcon() {
  return (
    <span
      aria-hidden
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--cta)_14%,transparent)] text-[var(--cta)]"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
    </span>
  );
}

type OfferProps = {
  label: string;
  text: string;
  action: string;
  expanded?: boolean;
  icon?: React.ReactNode;
  delayMs?: number;
  onAction: () => void;
  onDismiss: () => void;
  children?: React.ReactNode;
};
